/* eslint-disable jsx-a11y/anchor-is-valid */
/** @jsxImportSource @emotion/react */

import React from "react";
import { css } from "@emotion/react";
import { Box, Flex } from "reflexbox";
import lodash from "lodash";
import { ColorPicker } from "./ColorPicker";
import { CustomizerContext } from "./CustomizerContext";
import BalanceText from "react-balance-text";

export function AttributeControl({ attribute }) {
	let {
		AttributesState,
		updateAttributes,
		SelectedPresetsState,
		groupTypes,
		groupTags,
	} = React.useContext(CustomizerContext);

	let [attributes, setAttributes] = AttributesState;

	let [selectedPreset, setSelectedPreset] = SelectedPresetsState;

	var debouncedUpdate = lodash.debounce(updateAttributes, 400);

	if (attribute.hidden || attribute.disabled) return null;

	if (attribute.condition) {
		let hideForCondition = [];

		lodash.forEach(attribute.condition, (val, key) => {
			let matchingAttribute = lodash.find(attributes, { key: key });

			if (lodash.isObject(val)) {
				let { method, value } = val;

				if (method === "contains") {
					if (!lodash.get(matchingAttribute, "value", "").includes(value))
						hideForCondition.push(true);
				}
			} else if (val === "valued") {
				if (lodash.get(matchingAttribute, "value", "").length < 1)
					hideForCondition.push(true);
			} else {
				if (lodash.get(matchingAttribute, "value") !== val)
					hideForCondition.push(true);
			}
		});

		if (hideForCondition.length) return null;
	}

	let control = attribute.value;

	let attributeId = `field-${attribute.key}`;

	let help = lodash.get(attribute, "help");

	if (attribute.type === "text") {
		control = (
			<input
				className="form-control form-control-sm"
				type="search"
				id={attributeId}
				value={attribute.value}
				placeholder=""
				onChange={(e) => {
					setSelectedPreset(null);
					updateAttributes(
						{
							key: attribute.key,
							value: e.target.value,
						},
						false
					);
				}}
			></input>
		);
	}

	if (attribute.type === "group-type") {
		let selectedTypes = attribute.value.split(",");

		control = (
			<div
				css={css`
					& span {
						cursor: pointer;
						user-select: none;
						display: inline-block;
						background-color: transparent;
						padding: 1pt 4pt;
						border-radius: 3pt;
						font-size: 0.9em;
						vertical-align: middle;
						&:hover {
							background-color: #eee;
							color: #007bff;
						}
						&.selected {
							background-color: #007bff;
							color: white;
							&:hover {
								background-color: #005abb;
							}
						}
					}
				`}
			>
				{groupTypes.map((groupType, indx) => {
					let isSelected = selectedTypes.includes(groupType);

					return (
						<>
							{indx > 0 && " "}
							<span
								onClick={() => {
									let newSelectedTypes = selectedTypes;
									if (isSelected) {
										newSelectedTypes = lodash.without(
											newSelectedTypes,
											groupType
										);
									} else {
										newSelectedTypes.push(groupType);
									}

									updateAttributes(
										{
											key: attribute.key,
											value: lodash.compact(newSelectedTypes).join(","),
										},
										true
									);
								}}
								class={`${isSelected ? "selected" : ""}`}
							>
								{groupType}
							</span>
						</>
					);
				})}
			</div>
		);
	}

	if (attribute.type === "group-tags") {
		let selectedTags = attribute.value.split(",");

		control = (
			<div
				css={css`
					& span {
						cursor: pointer;
						user-select: none;
						display: inline-block;
						background-color: transparent;
						padding: 1pt 4pt;
						border-radius: 3pt;
						font-size: 0.9em;
						vertical-align: middle;
						&:hover {
							background-color: #eee;
							color: #007bff;
						}
						&.selected {
							background-color: #007bff;
							color: white;
							&:hover {
								background-color: #005abb;
							}
						}
					}
				`}
			>
				{groupTags.map((groupTag, indx) => {
					let isSelected = selectedTags.includes(groupTag);

					return (
						<>
							{indx > 0 && " "}
							<span
								onClick={() => {
									let newSelectedTags = selectedTags;
									if (isSelected) {
										newSelectedTags = lodash.without(newSelectedTags, groupTag);
									} else {
										newSelectedTags.push(groupTag);
									}

									updateAttributes(
										{
											key: attribute.key,
											value: lodash.compact(newSelectedTags).join(","),
										},
										true
									);
								}}
								class={`${isSelected ? "selected" : ""}`}
							>
								{groupTag}
							</span>
						</>
					);
				})}
			</div>
		);
	}

	if (attribute.type === "select") {
		control = (
			<select
				className="form-control form-control-sm"
				id={attributeId}
				value={attribute.value}
				onChange={(e) => {
					setSelectedPreset(null);
					console.log({
						key: attribute.key,
						value: e.target.value,
					});
					updateAttributes(
						{
							key: attribute.key,
							value: e.target.value,
						},
						true
					);
				}}
			>
				{attribute.options.map((opt) => {
					return (
						<option key={opt.value} value={opt.value}>
							{opt.name}
						</option>
					);
				})}
			</select>
		);
	}

	if (attribute.type === "color") {
		control = (
			<ColorPicker
				color={attribute.value}
				onChange={(newColor) => {
					setSelectedPreset(null);
					updateAttributes({ key: attribute.key, value: newColor.hex }, false);
				}}
			/>
		);
	}

	if (attribute.type === "number") {
		control = (
			<Flex>
				<Box width="20%" mr={2}>
					<input
						className="form-control form-control-sm"
						type="number"
						id={attributeId}
						value={attribute.value}
						onChange={(e) => {
							setSelectedPreset(null);
							updateAttributes(
								{
									key: attribute.key,
									value: e.target.value,
								},
								false
							);
						}}
					/>
				</Box>
				<Box>
					<input
						type="range"
						className="custom-range"
						min={lodash.get(attribute, "range.0")}
						max={lodash.get(attribute, "range.1")}
						id={attributeId + "range"}
						value={attribute.value}
						onChange={(e) => {
							setSelectedPreset(null);

							updateAttributes(
								{
									key: attribute.key,
									value: e.target.value,
								},
								false
							);
						}}
					></input>
				</Box>
			</Flex>
		);
	}

	if (attribute.type === "boolean") {
		control = (
			<div className="custom-control custom-switch">
				<input
					onChange={() => {
						setSelectedPreset(null);
						updateAttributes(
							{
								key: attribute.key,
								value: !attribute.value,
							},
							true
						);
					}}
					className="custom-control-input"
					checked={attribute.value}
					type="checkbox"
					id={attributeId}
				/>
				<label className="custom-control-label" htmlFor={attributeId}></label>
			</div>
		);
	}

	return (
		<>
			<Flex key={attribute.key} alignItems="center" pb={help ? 0 : 2}>
				<Box width="33%" textAlign="right">
					<label htmlFor={attributeId}>
						<BalanceText resize>{attribute.name}</BalanceText>
					</label>
				</Box>
				<Box ml={3} flex={1}>
					{control}
				</Box>
			</Flex>

			{help && (
				<Box ml={3} pl="33%" pb={2}>
					<Box
						px={2}
						css={css`
							font-size: 0.8em;
							font-style: italic;
							opacity: 0.66;
						`}
					>
						<BalanceText resize>{help}</BalanceText>
					</Box>
				</Box>
			)}
		</>
	);
}
